import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { catalogName } from "../../../Utils/catalogName";
import { formatDateToDDMYY } from "../../../Utils/dateFormat";

import { apiGetRequest } from "../../../Helpers/Requests";
import endpoints from "../../../Helpers/Endpoints";

// Fetches GET /api/supplier/dashboard and adapts it to the shape the container
// renders (TBBR-61).
//
// The API deliberately returns raw, unlocalized values — snake_case keys,
// `YYYY-MM` months, English service names, a structured due-date — because
// Admin_system has no request-language convention and every other endpoint
// behaves the same way. All the localizing happens here, in one place, so the
// container stays presentational.

/** `2026-07` → "Jul '26" / "יולי 26" (short) or "July 2026" (full). */
const formatMonth = (ym, lang, style) => {
  if (!ym) return "";
  const [year, month] = ym.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, 1));
  const opts =
    style === "full"
      ? { month: "long", year: "numeric", timeZone: "UTC" }
      : style === "nameOnly"
        ? { month: "short", timeZone: "UTC" }
        : { month: "short", year: "2-digit", timeZone: "UTC" };
  return new Intl.DateTimeFormat(lang, opts).format(date);
};

const toDateOnly = (value) => {
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const daysFromToday = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((date - today) / 86400000);
};

/** Days past due at which an upcoming job asks "was it done?" (COMY1-401). */
export const LONG_OVERDUE_DAYS = 7;

// Statuses that are finished, so never overdue. The API's upcoming-jobs queue
// only sends open work today; this guards against that ever widening.
const CLOSED_JOB_STATUSES = ["completed", "canceled", "cancelled"];

const pad2 = (n) => String(n).padStart(2, "0");
/** DD/MM/YYYY in both languages. */
const formatDayMonthYear = (date) =>
  `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;

/**
 * Days a job is past its due date, or 0 when it isn't overdue. Admin_system
 * labels SUPPLIER_FOUND / CONFIRMED "scheduled" whatever the date, so the
 * override lives here. Only a concrete date can be missed — 'flexible' and
 * 'weekends' windows are never overdue.
 */
const overdueDaysOf = (job) => {
  const due = job.due;
  if (!due || due.type !== "date" || !due.value) return 0;
  if (CLOSED_JOB_STATUSES.includes(job.status)) return 0;
  const date = toDateOnly(due.value);
  if (!date) return 0;
  const delta = daysFromToday(date);
  return delta < 0 ? -delta : 0;
};

export const useSupplierDashboard = () => {
  const { t, i18n } = useTranslation("page-supplier-dashboard");
  // Service names are stored in English and translated client-side; the English
  // name IS the key in the locale file. Which file depends on what the API
  // could resolve: a real service type lands in services.json, while
  // visit-only / category-only orders (which carry no service line at all) fall
  // back to a category name, and those live in categories.json. Try both.
  const { t: tServices } = useTranslation("services");
  const { t: tCategories } = useTranslation("categories");

  const [raw, setRaw] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiGetRequest(endpoints.getSupplierDashboard);
      setRaw(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const lang = (i18n.language || "en").toLowerCase().startsWith("he")
    ? "he"
    : "en";

  const data = useMemo(() => {
    if (!raw) return null;

    // Today the API ranks categories (no name_he); if it ever sends a service
    // the catalog agent created, its own Hebrew name is used (no JSON key).
    const serviceName = (name, nameHe) => {
      if (!name) return name;
      if (nameHe) {
        return catalogName(tServices, name, { ns: "services", nameHe, language: lang });
      }
      const opts = { nsSeparator: false, keySeparator: false };
      const MISS = "\u0000";
      const fromServices = tServices(name, { ...opts, defaultValue: MISS });
      if (fromServices !== MISS) return fromServices;
      return tCategories(name, { ...opts, defaultValue: name });
    };

    const sparkPoints = (points) =>
      (points || []).map((p) => ({
        value: p.value ?? 0,
        label: formatMonth(p.month, lang, "short"),
        full: formatMonth(p.month, lang, "full"),
      }));

    // Quality trends carry null for "no orders that month" — keep the null so
    // the cell renders a dash instead of a fabricated 0%.
    const trendPairs = (points) =>
      (points || []).map((p) => [formatMonth(p.month, lang, "nameOnly"), p.value]);

    // Services have no scheduled time — only a window the member picked — so
    // "when" is a derived day, not an appointment. Say the honest thing.
    const whenLabel = (due) => {
      if (!due || due.type === "flexible") return t("when.flexible");
      if (due.type === "weekends") return t("when.weekends");
      const date = due.value ? toDateOnly(due.value) : null;
      if (!date) return t("when.flexible");
      const delta = daysFromToday(date);
      if (delta === 0) return t("when.today");
      if (delta === 1) return t("when.tomorrow");
      // Past dates are rendered by the container from `dueDate` +
      // `overdueDays` (DD/MM/YYYY · N days ago, with <bdi> isolation).
      if (delta < 0) return null;
      return formatDateToDDMYY(date);
    };

    const quality = (block) => ({
      metrics: {
        fulfillment: {
          raw: block.metrics.fulfillment.raw,
          trend: trendPairs(block.metrics.fulfillment.trend),
        },
        approval: {
          raw: block.metrics.approval.raw,
          trend: trendPairs(block.metrics.approval.trend),
        },
        completion: {
          raw: block.metrics.completion.raw,
          trend: trendPairs(block.metrics.completion.trend),
        },
        cancellation: {
          raw: block.metrics.cancellation.raw,
          trend: trendPairs(block.metrics.cancellation.trend),
        },
      },
      communities: (block.communities || []).map((c) => ({
        name: c.name,
        image: c.image,
        fulfillment: c.fulfillment,
        approvalHours: c.approval_hours,
        cancellationRate: c.cancellation_rate,
      })),
    });

    return {
      currencySymbol: raw.currency_symbol,
      hasServices: raw.has_services,
      hasProducts: raw.has_products,

      sales: {
        countAllTime: raw.sales.count_all_time,
        moneyAllTime: raw.sales.money_all_time,
        countThisMonth: raw.sales.count_this_month,
        moneyThisMonth: raw.sales.money_this_month,
        monthly: sparkPoints(raw.sales.monthly),
      },

      earnings: {
        allTime: raw.earnings.all_time,
        thisMonth: raw.earnings.this_month,
        // From the DB, not Grow: sum of the supplier's share totals (null only
        // when there are no rev-share rows). There is no payout ledger yet.
        // The container shows a dash for null and disables Withdraw unless
        // this is > 0.
        currentBalance: raw.earnings.current_balance,
        monthly: sparkPoints(raw.earnings.monthly),
      },

      actions: {
        pendingApproval: raw.actions.pending_approval,
        inProgress: raw.actions.in_progress,
        dueToday: raw.actions.due_today,
        overdue: raw.actions.overdue,
      },

      upcomingJobs: (raw.upcoming_jobs || []).map((job) => {
        const overdueDays = overdueDaysOf(job);
        const dueDate = job.due?.value ? toDateOnly(job.due.value) : null;
        return {
          id: job.id,
          // 'service' | 'product' — My Jobs needs it to open the right order.
          itemType: job.item_type || "service",
          community: job.community?.name || "",
          when: whenLabel(job.due),
          dueDate: overdueDays && dueDate ? formatDayMonthYear(dueDate) : null,
          overdueDays,
          // The API's own bucket, kept so the row knows whether the supplier
          // has accepted the job (only accepted work can have been "done").
          baseStatus: job.status,
          status: overdueDays ? "overdue" : job.status,
        };
      }),

      communities: {
        active: raw.communities.active,
        activeAvatars: raw.communities.active_avatars,
        pending: (raw.communities.pending || []).map((c) => ({
          // The id is what lets the Join button deep-link to this specific
          // invitation on My Communities.
          id: c.id,
          name: c.name,
          members: c.members,
          type: c.type,
          image: c.image,
        })),
      },

      topServices: (raw.top_services || []).map((s) => ({
        name: serviceName(s.name, s.name_he),
        image: s.image,
        count: s.count,
        revenue: s.revenue,
      })),
      topProducts: (raw.top_products || []).map((p) => ({
        name: p.name,
        image: p.image,
        count: p.count,
        revenue: p.revenue,
      })),
      topCommunities: (raw.top_communities || []).map((c) => ({
        name: c.name,
        image: c.image,
        revenue: c.revenue,
      })),

      quality: {
        services: quality(raw.quality.services),
        products: quality(raw.quality.products),
      },
    };
  }, [raw, lang, t, tServices, tCategories]);

  return { data, isLoading, error, refetch: fetchDashboard };
};

export default useSupplierDashboard;
